import { useContext } from 'react';
import type { FILTER_OPTIONS_DIVIDER } from '../constants';
import { SearchFilterV2StoreContext } from '../store';
import type {
	FilterDropdownConfigDate,
	FilterDropdownConfigList,
	FilterDropdownConfigString,
	FilterOperator,
	FilterOption,
	FilterValue,
} from '../types';
import { FilterDropdownType } from '../types';
import { FilterDropdownAI } from './FilterDropdownAI';
import { FilterDropdownDate } from './FilterDropdownDate';
import { FilterDropdownList } from './FilterDropdownList';
import { FilterDropdownNestedList } from './FilterDropdownNestedList';
import { FilterDropdownString } from './FilterDropdownString';

interface FilterDropdownProps {
	value: FilterValue;
	filterOption: FilterOption;
	onChange: (value: FilterValue, close: boolean) => void;
	searchTerm?: string;
	filterOptions?: Array<FilterOption | typeof FILTER_OPTIONS_DIVIDER>;
	operator?: FilterOperator;
}

export function FilterDropdown({
	value,
	filterOption,
	onChange,
	searchTerm,
	filterOptions,
	operator,
}: FilterDropdownProps) {
	const searchFilterV2Store = useContext(SearchFilterV2StoreContext);

	switch (filterOption.filterDropdownConfig.dropdownType) {
		case FilterDropdownType.AI: {
			return (
				<FilterDropdownAI
					initialSearchTerm={searchTerm ?? ''}
					onChange={onChange}
					filterOptions={filterOptions ?? []}
				/>
			);
		}
		case FilterDropdownType.NestedList: {
			if (!operator) {
				return null;
			}
			return (
				<FilterDropdownNestedList
					value={value}
					getItems={filterOption.filterDropdownConfig.getItems}
					onChange={onChange}
					operator={operator}
				/>
			);
		}
		case FilterDropdownType.List: {
			const config =
				filterOption.filterDropdownConfig as FilterDropdownConfigList;

			return (
				<FilterDropdownList
					value={value}
					filterType={config.filterType}
					renderMenuItem={config.renderMenuItem}
					getItems={config.getItems}
					getItemsById={config.getItemsById}
					excludeItems={searchFilterV2Store.excludeItems?.[filterOption.type]}
					onChange={onChange}
					searchPlaceholder={config.searchPlaceholder}
					hasIsNotSetOption={config.hasIsNotSetOption}
					hasIsSetOption={config.hasIsSetOption}
					filterOptionType={filterOption.type}
				/>
			);
		}
		case FilterDropdownType.AI: {
			// TODO AI dropdown
			return null;
		}
		case FilterDropdownType.Number:
		case FilterDropdownType.String: {
			const config =
				filterOption.filterDropdownConfig as FilterDropdownConfigString;

			return (
				<FilterDropdownString
					value={value}
					onChange={onChange}
					inputPlaceholder={config.inputPlaceholder}
					hasIsNotSetOption={config.hasIsNotSetOption}
					hasIsSetOption={config.hasIsSetOption}
					prefilledOptions={config.prefilledOptions}
				/>
			);
		}
		case FilterDropdownType.Date: {
			const config =
				filterOption.filterDropdownConfig as FilterDropdownConfigDate;

			return (
				<FilterDropdownDate
					value={value}
					onChange={onChange}
					getItems={config.getItems}
					filterType={config.filterType}
				/>
			);
		}
		default: {
			// eslint-disable-next-line no-console
			console.error('Invalid dropdown type');
			return null;
		}
	}
}
